//@flow
import {saveStoreInformationService} from '../promises';
import type {SaveInformationRequest} from '../api';
import {useActions} from '../../../../utils/globalStore';

import {actions as UiActions} from '../../../ui/duck';
import {useState, useEffect} from 'react';
import {uploadImageService} from '../../../file-storage/promises';

type UpdateStoreBackgroundParams = {
  backgroundColor?: string,
  backgroundEnabled?: boolean,
  backgroundPatternFile?: File,
  backgroundPatternFileId?: string,
  backgroundPhotoDesktopFile?: File,
  backgroundPhotoMobileFileId?: string,
  backgroundPhotoMobileFile?: File,
  backgroundPhotoDesktopFileId?: string,
  backgroundType?: 'COLOR' | 'PATTERN' | 'PHOTO',
};

export function useCustomStoreBackgroundActions() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const fetchStoreInformation = useActions(UiActions.fetchStoreInformation);

  const updateStoreBackground = async (params: UpdateStoreBackgroundParams) => {
    setIsLoading(true);

    try {
      if (!params.backgroundEnabled) {
        const response = await saveStoreInformationService({
          backgroundEnabled: false,
        }).catch(error => {
          setError(`Ocurrió un error al deshabilitar el fondo`);
        });

        if (response) {
          fetchStoreInformation();
        }
        return;
      }

      const storeInformationRequest: SaveInformationRequest = {
        backgroundType: params.backgroundType,
        backgroundEnabled: true,
      };

      if (params.backgroundType === 'COLOR') {
        storeInformationRequest.backgroundColor = params.backgroundColor;

        const response = await saveStoreInformationService(
          storeInformationRequest
        ).catch(error => {
          setError(`Ocurrió un error al guardar el color de fondo`);
        });
        if (response) {
          fetchStoreInformation();
        }
        return;
      }

      if (params.backgroundType === 'PATTERN') {
        if (!params.backgroundPatternFile && !params.backgroundPatternFileId) {
          setError('Debes seleccionar un archivo');
          return;
        }

        let patternImageId = params.backgroundPatternFileId;

        if (params.backgroundPatternFile) {
          const [patternImage] = await uploadImageService(
            params.backgroundPatternFile,
            'ui/store-background'
          );

          patternImageId = patternImage.id;
        }

        storeInformationRequest.backgroundPatternId = patternImageId;

        const response = await saveStoreInformationService(
          storeInformationRequest
        ).catch(error => {
          setError(`Ocurrió un error al guardar el patrón de fondo`);
        });

        if (response) {
          fetchStoreInformation();
        }

        return;
      }

      if (params.backgroundType === 'PHOTO') {
        if (
          !params.backgroundPhotoDesktopFile &&
          !params.backgroundPhotoDesktopFileId
        ) {
          setError('Debes seleccionar una imagen para escritorio');
          return;
        }

        if (
          !params.backgroundPhotoMobileFile &&
          !params.backgroundPhotoMobileFileId
        ) {
          setError('Debes seleccionar una imagen para celular');
          return;
        }

        let desktopImageId = params.backgroundPhotoDesktopFileId;
        let mobileImageId = params.backgroundPhotoMobileFileId;

        if (params.backgroundPhotoDesktopFile) {
          const [desktopImage] = await uploadImageService(
            params.backgroundPhotoDesktopFile,
            'ui/store-background'
          );

          desktopImageId = desktopImage.id;
        }

        if (params.backgroundPhotoMobileFile) {
          const [mobileImage] = await uploadImageService(
            params.backgroundPhotoMobileFile,
            'ui/store-background'
          );

          mobileImageId = mobileImage.id;
        }

        storeInformationRequest.backgroundPhotoDesktopId = desktopImageId;
        storeInformationRequest.backgroundPhotoMobileId = mobileImageId;

        const response = await saveStoreInformationService(
          storeInformationRequest
        ).catch(error => {
          setError(`Ocurrió un error al guardar la imagen de fondo`);
        });

        if (response) {
          fetchStoreInformation();
        }
      }
    } catch (error) {
      console.error(error);
      setError('Ocurrió un error al guardar la información');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let timeout;
    if (error) {
      timeout = setTimeout(() => {
        setError('');
      }, 5000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  return {
    isLoading,
    error,
    updateStoreBackground,
  };
}
